import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Badge, Modal, Spinner, Tooltip, ConfirmDialog } from '../../components/UI';
import { googleSheetAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineDocumentText, HiOutlineClipboard, HiOutlineEye, HiOutlineEyeOff,
  HiOutlineTrash, HiOutlinePlus, HiOutlineRefresh, HiOutlineCheck,
  HiOutlineExclamation, HiOutlineCode, HiOutlineCloudUpload,
  HiOutlineClipboardList, HiOutlineLightningBolt, HiOutlineDocumentDuplicate
} from 'react-icons/hi';

const GoogleSheetIntegration = () => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [copiedScript, setCopiedScript] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);

  // Fetch API keys
  const { data: apiKeysData, isLoading: loadingKeys } = useQuery({
    queryKey: ['googleSheetApiKeys'],
    queryFn: async () => {
      const response = await googleSheetAPI.getApiKeys();
      return response.data;
    }
  });

  // Fetch script
  const { data: scriptData, isLoading: loadingScript } = useQuery({
    queryKey: ['googleSheetScript', selectedKey?._id],
    queryFn: async () => {
      const response = await googleSheetAPI.getScript(selectedKey?._id);
      console.log('Script Response:', response.data);
      console.log('Script Length:', response.data?.data?.script?.length);
      return response.data;
    },
    enabled: showScriptModal
  });

  // Create API key mutation
  const createMutation = useMutation({
    mutationFn: (data) => googleSheetAPI.createApiKey(data),
    onSuccess: (response) => {
      console.log('Full Response:', response);
      console.log('Response Data:', response.data);
      // The API returns { success: true, data: { apiKey, name, ... } }
      // Axios wraps it, so response.data = { success: true, data: {...} }
      // We need response.data.data to get the actual key object
      const keyData = response.data?.data || response.data;
      console.log('Key Data:', keyData);
      setCreatedKey(keyData);
      queryClient.invalidateQueries(['googleSheetApiKeys']);
      toast.success('API Key generated successfully!');
    },
    onError: (error) => {
      console.error('API Key Error:', error);
      toast.error(error.response?.data?.message || 'Failed to create API key');
    }
  });

  // Delete API key mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => googleSheetAPI.deleteApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['googleSheetApiKeys']);
      toast.success('API Key deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete API key');
    }
  });

  // Copy to clipboard
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Copy full script
  const handleCopyScript = async () => {
    if (scriptData?.data?.script) {
      await copyToClipboard(scriptData.data.script, 'Script');
      setCopiedScript(true);
      setTimeout(() => setCopiedScript(false), 3000);
    }
  };

  // Handle create key
  const handleCreateKey = () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }
    createMutation.mutate({ name: newKeyName.trim() });
  };

  // Handle delete key
  const handleDeleteKey = (key) => {
    setKeyToDelete(key);
    setShowDeleteModal(true);
  };

  const confirmDeleteKey = () => {
    if (keyToDelete) {
      deleteMutation.mutate(keyToDelete._id);
    }
    setShowDeleteModal(false);
    setKeyToDelete(null);
  };

  // Handle show script
  const handleShowScript = (key) => {
    setSelectedKey(key);
    setShowScriptModal(true);
  };

  const apiKeys = apiKeysData?.data || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Google Sheets Integration
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Connect Google Sheets to automatically import leads into TeleValley
          </p>
        </div>
        <Button
          variant="primary"
            icon={<HiOutlinePlus className="w-5 h-5" />}
            onClick={() => {
              setNewKeyName('');
              setCreatedKey(null);
              setShowCreateModal(true);
            }}
          >
            Generate API Key
          </Button>
        </div>

        {/* How it works */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <HiOutlineLightningBolt className="w-5 h-5 text-blue-600" />
            How it Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Generate API Key</h3>
                <p className="text-sm text-gray-600">Create a unique API key for your organization</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Add Script to Sheet</h3>
                <p className="text-sm text-gray-600">Copy our script to your Google Sheet</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Leads Auto-Import</h3>
                <p className="text-sm text-gray-600">New leads are automatically sent to TeleValley</p>
              </div>
            </div>
          </div>
        </Card>

        {/* API Keys List */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <HiOutlineDocumentDuplicate className="w-5 h-5" />
              Your API Keys
            </h2>
            <Badge variant={apiKeys.length > 0 ? 'success' : 'default'}>
              {apiKeys.length} key{apiKeys.length !== 1 ? 's' : ''}
            </Badge>
          </div>

          {loadingKeys ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8">
              <HiOutlineCloudUpload className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No API keys generated yet</p>
              <p className="text-gray-400 text-sm">Generate an API key to start integrating with Google Sheets</p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div
                  key={key._id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <HiOutlineCode className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{key.name}</h3>
                      <p className="text-sm text-gray-500">
                        Key: <span className="font-mono">{key.keyPrefix}</span>
                      </p>
                      <p className="text-xs text-gray-400">
                        Created: {new Date(key.createdAt).toLocaleDateString()}
                        {key.lastUsedAt && ` • Last used: ${new Date(key.lastUsedAt).toLocaleDateString()}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={key.isActive ? 'success' : 'default'}>
                      {key.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{key.totalLeadsImported || 0}</span> leads imported
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<HiOutlineDocumentText className="w-4 h-4" />}
                      onClick={() => handleShowScript(key)}
                    >
                      Get Script
                    </Button>
                    <Tooltip text="Delete API Key">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                        onClick={() => handleDeleteKey(key)}
                      />
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Create API Key Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setCreatedKey(null);
            setNewKeyName('');
          }}
          title={createdKey ? 'API Key Generated!' : 'Generate New API Key'}
          size="md"
        >
          {!createdKey ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Key Name
                </label>
                <Input
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production Sheet, Marketing Leads"
                  helperText="Give your API key a descriptive name"
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">What happens next?</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• A unique API key will be generated</li>
                  <li>• Copy the key immediately (shown only once)</li>
                  <li>• Add the key to your Google Sheet script</li>
                </ul>
              </div>
              <div className="flex gap-3 justify-end">
                <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateKey}
                  loading={createMutation.isPending}
                >
                  Generate Key
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-800 mb-2">
                  <HiOutlineCheck className="w-5 h-5" />
                  <span className="font-medium">API Key Generated Successfully!</span>
                </div>
                <p className="text-sm text-green-700">
                  Copy this key now - it will not be shown again!
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Key Name
                </label>
                <Input value={createdKey?.name || ''} disabled />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your API Key
                </label>
                <div className="relative">
                  <Input
                    value={createdKey?.apiKey || ''}
                    type={showApiKey ? 'text' : 'password'}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? (
                      <HiOutlineEyeOff className="w-5 h-5" />
                    ) : (
                      <HiOutlineEye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Debug info - remove after testing */}
              {createdKey?.apiKey && (
                <div className="bg-gray-100 p-2 rounded text-xs font-mono break-all">
                  <strong>API Key (visible):</strong> {createdKey.apiKey}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="primary"
                  className="flex-1"
                  icon={<HiOutlineClipboard className="w-5 h-5" />}
                  onClick={() => copyToClipboard(createdKey?.apiKey || '', 'API Key')}
                >
                  Copy API Key
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    handleShowScript({ _id: createdKey?._id, name: createdKey?.name });
                  }}
                >
                  Get Script
                </Button>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Important:</strong> Store this API key securely. You won't be able to see it again.
                </p>
              </div>
            </div>
          )}
        </Modal>

        {/* Script Modal */}
        <Modal
          isOpen={showScriptModal}
          onClose={() => {
            setShowScriptModal(false);
            setSelectedKey(null);
            setCopiedScript(false);
          }}
          title="Google Apps Script"
          size="xl"
        >
          {loadingScript ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Debug: Check if script exists */}
              {!scriptData?.data?.script && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  <strong>Debug:</strong> No script data received.
                  <pre className="mt-2 text-xs bg-red-100 p-2 rounded overflow-auto max-h-32">
                    {JSON.stringify(scriptData, null, 2)}
                  </pre>
                </div>
              )}
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-3">Setup Instructions</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                  {scriptData?.data?.instructions?.map((instruction, index) => (
                    <li key={index} className="flex gap-2">
                      <span className="font-medium">{instruction.step}.</span>
                      <div>
                        <span className="font-medium">{instruction.title}:</span>{' '}
                        <span className="text-blue-700">{instruction.description}</span>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Column Mapping Info */}
              {selectedKey && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Column Mapping for "{selectedKey.name}"</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                    <div><span className="text-gray-500">Name:</span> Column {selectedKey?.columnMapping?.name || 'A'}</div>
                    <div><span className="text-gray-500">Phone:</span> Column {selectedKey?.columnMapping?.phone || 'B'}</div>
                    <div><span className="text-gray-500">Email:</span> Column {selectedKey?.columnMapping?.email || 'C'}</div>
                    <div><span className="text-gray-500">Status:</span> Column {selectedKey?.columnMapping?.status || 'D'}</div>
                    <div><span className="text-gray-500">Priority:</span> Column {selectedKey?.columnMapping?.priority || 'E'}</div>
                    <div><span className="text-gray-500">Source:</span> Column {selectedKey?.columnMapping?.source || 'F'}</div>
                    <div><span className="text-gray-500">Notes:</span> Column {selectedKey?.columnMapping?.notes || 'G'}</div>
                    <div><span className="text-gray-500">Stage:</span> Column {selectedKey?.columnMapping?.stage || 'H'}</div>
                  </div>
                  <div className="mt-3 text-xs text-gray-500 space-y-1">
                    <p>• Status accepts <code className="bg-gray-200 px-1 rounded">hot</code>, <code className="bg-gray-200 px-1 rounded">cold</code>, or <code className="bg-gray-200 px-1 rounded">warm</code> — defaults to <code className="bg-gray-200 px-1 rounded">cold</code> when the cell is empty.</p>
                    <p>• Stage defaults to <code className="bg-gray-200 px-1 rounded">new_lead</code> when the cell is empty. Set it to any of your pipeline stage keys.</p>
                  </div>
                </div>
              )}

              {/* Script */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Apps Script Code
                  </label>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={copiedScript ? <HiOutlineCheck className="w-4 h-4" /> : <HiOutlineClipboard className="w-4 h-4" />}
                    onClick={handleCopyScript}
                  >
                    {copiedScript ? 'Copied!' : 'Copy Script'}
                  </Button>
                </div>
                <textarea
                  readOnly
                  value={scriptData?.data?.script || 'Loading script...'}
                  className="w-full h-96 p-4 bg-gray-900 text-gray-100 font-mono text-xs rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y"
                  style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                    whiteSpace: 'pre',
                    overflowWrap: 'normal',
                    overflowX: 'auto'
                  }}
                  onClick={(e) => e.target.select()}
                />
              </div>

              {/* Tips */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 mb-2 flex items-center gap-2">
                  <HiOutlineExclamation className="w-5 h-5" />
                  Important Tips
                </h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Replace <code className="bg-yellow-100 px-1 rounded">YOUR_API_KEY_HERE</code> with your actual API key</li>
                  <li>• Status column accepts <code className="bg-yellow-100 px-1 rounded">hot</code>, <code className="bg-yellow-100 px-1 rounded">cold</code>, or <code className="bg-yellow-100 px-1 rounded">warm</code> (defaults to <code className="bg-yellow-100 px-1 rounded">cold</code>)</li>
                  <li>• Stage column defaults to <code className="bg-yellow-100 px-1 rounded">new_lead</code> when empty — set it to one of your pipeline stage keys</li>
                  <li>• Set up triggers in Apps Script: Triggers → Add Trigger → onFormSubmit</li>
                  <li>• Test the connection using the "Test Connection" menu option</li>
                  <li>• Check the Logs sheet for debugging information</li>
                </ul>
              </div>

              <div className="flex gap-3 justify-end">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowScriptModal(false);
                    setSelectedKey(null);
                    setCopiedScript(false);
                  }}
                >
                  Close
                </Button>
                <Button
                  variant="primary"
                  icon={<HiOutlineClipboard className="w-5 h-5" />}
                  onClick={handleCopyScript}
                >
                  Copy Full Script
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Quick Start Guide */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <HiOutlineDocumentText className="w-5 h-5" />
            Quick Start Guide
          </h2>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Generate API Key</h3>
                <p className="text-sm text-gray-600">Click "Generate API Key" button above to create a new key for your organization.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Copy the Script</h3>
                <p className="text-sm text-gray-600">Click "Get Script" next to your API key to view the complete Google Apps Script code.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Add to Google Sheet</h3>
                <p className="text-sm text-gray-600">Open your Google Sheet → Extensions → Apps Script → Paste the code and save.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold flex-shrink-0">
                4
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Set Up Trigger</h3>
                <p className="text-sm text-gray-600">In Apps Script, click Triggers → Add Trigger → Choose "onFormSubmit" → From spreadsheet → On form submit.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold flex-shrink-0">
                5
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Test & Go Live</h3>
                <p className="text-sm text-gray-600">Run "testConnection" to verify setup. New form submissions will automatically create leads in TeleValley!</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Delete Confirmation Modal */}
        <ConfirmDialog
          isOpen={showDeleteModal}
          onClose={() => { setShowDeleteModal(false); setKeyToDelete(null); }}
          onConfirm={confirmDeleteKey}
          title="Delete API Key"
          message={keyToDelete ? `Are you sure you want to delete "${keyToDelete.name}"? This action cannot be undone and any integrations using this key will stop working.` : 'Are you sure you want to delete this API key?'}
          confirmText="Yes, Delete"
          cancelText="Cancel"
          variant="danger"
          icon={HiOutlineTrash}
          loading={deleteMutation.isPending}
        />
      </div>
  );
};

export default GoogleSheetIntegration;